import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Check, Mail, Clock, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending: { label: 'En attente paiement', cls: 'bg-[#B8922A]/20 text-[#B8922A] border-[#B8922A]' },
  paid: { label: 'Payé · code actif', cls: 'bg-[#1E7A6A]/20 text-[#1E7A6A] border-[#1E7A6A]' },
  used: { label: 'Utilisé', cls: 'bg-[#332724]/20 text-[#332724] border-[#332724]' },
  expired: { label: 'Expiré', cls: 'bg-red-100 text-red-700 border-red-400' },
};

const AdminGiftCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/gift-cards`, { withCredentials: true });
      setCards(data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/gift-cards/${id}`, { status }, { withCredentials: true });
      toast.success(status === 'paid' ? 'Marqué payé · emails envoyés !' : 'Statut mis à jour');
      fetch();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copié`);
  };

  const filtered = filter === 'all' ? cards : cards.filter((c) => c.status === filter);

  const stats = {
    total: cards.length,
    pending: cards.filter((c) => c.status === 'pending').length,
    paid: cards.filter((c) => c.status === 'paid').length,
    used: cards.filter((c) => c.status === 'used').length,
    revenue: cards.filter((c) => c.status === 'paid' || c.status === 'used').reduce((a, b) => a + b.amount, 0),
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1
              className="text-3xl text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Bons cadeaux
            </h1>
            <p className="text-sm text-[#332724]/70 mt-1">
              Gérez les commandes de bons cadeaux et validez les paiements Square.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gold" data-testid="stat-total">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Total</p>
            <p className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gold" data-testid="stat-pending">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">En attente</p>
            <p className="text-2xl text-[#B8922A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.pending}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gold" data-testid="stat-paid">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Payés</p>
            <p className="text-2xl text-[#1E7A6A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.paid}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gold" data-testid="stat-used">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Utilisés</p>
            <p className="text-2xl text-[#332724]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.used}</p>
          </div>
          <div className="bg-[#5C2018] text-[#FDFAF4] p-4 rounded-xl" data-testid="stat-revenue">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">CA confirmé</p>
            <p className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>{stats.revenue}€</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'all', label: 'Toutes' },
            { id: 'pending', label: 'En attente' },
            { id: 'paid', label: 'Payées' },
            { id: 'used', label: 'Utilisées' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] transition-colors ${
                filter === f.id
                  ? 'bg-[#5C2018] text-[#FDFAF4]'
                  : 'bg-white text-[#332724] border border-[#B8922A]/30 hover:border-[#5C2018]'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center text-[#332724]/70 py-12">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl border border-gold text-center">
            <Gift className="mx-auto text-[#B8922A]/50 mb-3" size={48} />
            <p className="text-[#332724]/70">Aucune commande {filter !== 'all' ? `(${filter})` : ''} pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => {
              const badge = STATUS_BADGES[c.status] || STATUS_BADGES.pending;
              return (
                <div key={c.id} className="bg-white p-5 rounded-2xl border border-gold" data-testid={`card-${c.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    {/* Code & status */}
                    <div className="md:col-span-3">
                      <button
                        onClick={() => copyCode(c.code)}
                        className="font-mono text-sm text-[#5C2018] bg-[#FDFAF4] px-3 py-1.5 rounded border border-[#B8922A]/30 hover:border-[#5C2018] transition-colors"
                        title="Cliquer pour copier"
                        data-testid={`code-${c.id}`}
                      >
                        {c.code}
                      </button>
                      <div className="mt-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs border ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="md:col-span-1">
                      <p className="text-xs text-[#332724]/60 uppercase tracking-wider">Montant</p>
                      <p className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                        {c.amount}€
                      </p>
                    </div>

                    {/* Buyer & recipient */}
                    <div className="md:col-span-5 text-sm text-[#332724]">
                      <p><strong>Acheteur :</strong> {c.buyer_name} · <a href={`mailto:${c.buyer_email}`} className="text-[#1E7A6A] hover:underline">{c.buyer_email}</a></p>
                      {c.recipient_name && (
                        <p><strong>Bénéficiaire :</strong> {c.recipient_name}
                          {c.recipient_email && (<> · <a href={`mailto:${c.recipient_email}`} className="text-[#1E7A6A] hover:underline">{c.recipient_email}</a></>)}
                        </p>
                      )}
                      {c.message && (
                        <p className="italic text-[#332724]/80 mt-1">« {c.message} »</p>
                      )}
                      <p className="text-xs text-[#332724]/60 mt-1">
                        Créé le {new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {c.expires_at && ` · Expire le ${new Date(c.expires_at).toLocaleDateString('fr-FR')}`}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="md:col-span-3 flex flex-col gap-2">
                      {c.status === 'pending' && (
                        <button
                          onClick={() => updateStatus(c.id, 'paid')}
                          className="inline-flex items-center justify-center gap-2 bg-[#1E7A6A] hover:bg-[#155349] text-[#FDFAF4] px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] transition-colors"
                          data-testid={`mark-paid-${c.id}`}
                        >
                          <Check size={14} /> Marquer payé
                        </button>
                      )}
                      {c.status === 'paid' && (
                        <button
                          onClick={() => updateStatus(c.id, 'used')}
                          className="inline-flex items-center justify-center gap-2 bg-[#332724] hover:bg-[#1f1614] text-[#FDFAF4] px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] transition-colors"
                          data-testid={`mark-used-${c.id}`}
                        >
                          Marquer utilisé
                        </button>
                      )}
                      {c.status === 'pending' && (
                        <p className="text-xs text-[#B8922A] flex items-center gap-1 justify-center">
                          <Clock size={12} /> Validez après réception du paiement Square
                        </p>
                      )}
                      {c.status === 'paid' && (
                        <p className="text-xs text-[#1E7A6A] flex items-center gap-1 justify-center">
                          <Mail size={12} /> Emails envoyés à l'acheteur et au bénéficiaire
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Aide / mode opératoire */}
        <div className="bg-[#FDFAF4] border border-gold rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">Mode opératoire</p>
          <ol className="text-sm text-[#332724] space-y-1 list-decimal pl-5">
            <li>Le client commande un bon sur le site → statut <strong>« En attente »</strong> + il est redirigé vers Square.</li>
            <li>Vous recevez un email FormSubmit + voyez la commande ci-dessus.</li>
            <li>Quand vous voyez le paiement reçu dans votre dashboard Square, cliquez <strong>« Marquer payé »</strong>.</li>
            <li>Les emails de confirmation (acheteur + bénéficiaire) sont envoyés automatiquement avec le code.</li>
            <li>Quand le bon est utilisé en boutique, cliquez <strong>« Marquer utilisé »</strong> pour le clôturer.</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminGiftCards;
